/**
 * 情报总览页面 — Dashboard（批 3 包 B 改造，对齐概念稿 overview.html）。
 *
 * 信息架构（对齐设计稿：4 指标卡 → 预警流+漏斗 g83 → 迷你矩阵+最近动态 g83）：
 *   1. 指标卡 ×4：AI 可见性均值 / 本周新增预警 / 投放内容收录率 / 本月投放费用
 *   2. 预警流（左 2/3，F1 预警引擎）+ 三层漏斗（右 1/3，F5 收录管道）
 *   3. 迷你矩阵（左 2/3，品牌×平台热力速览）+ 最近动态（右 1/3，保留原有）
 *   4. 保留下移：产品 GHS 排行 + 投放费用概览（不删现有功能）
 *
 * 数据源与降级策略：
 *   - GET /api/dashboard?company_id= 主数据（阻断式，失败显示整页空态）
 *   - GET /api/alerts + /api/alerts/summary（预警引擎并行开发中，失败仅预警卡空态）
 *   - GET /api/index-ledger/summary?product_id=（批 5 端点，404/空 → 漏斗卡空态，不报错）
 *   - GET /api/ai-visibility?company_id=（迷你矩阵 + 可见性均值，失败仅矩阵卡空态）
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Button,
  Link,
} from "@mui/material";
import {
  VisibilityOutlined,
  NotificationsActiveOutlined,
  FactCheckOutlined,
  PaymentsOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import GhsRing from "../components/GhsRing";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import UpdateCapsule from "../components/UpdateCapsule";
import AlertItem from "../components/AlertItem";
import FunnelView from "../components/FunnelView";
import { getDashboard } from "../api/dashboard";
import { getAlerts, getAlertSummary } from "../api/alerts";
import { getIndexLedgerSummary } from "../api/indexLedger";
import { getAiVisibility } from "../api/visibility";
import { useCompanyStore } from "../store/companyStore";
import {
  DELIVERY_STATUS_MAP,
  PLATFORM_MAP,
  HEAT_LEVELS,
  heatLevel,
  platformLabel,
} from "../utils/constants";
import { formatCurrency, formatRelativeTime } from "../utils/format";
import type {
  AiVisibility,
  AlertItem as AlertItemData,
  AlertSummary,
  DashboardData,
  LedgerSummary,
} from "../types";

/** 平台列顺序（对齐 PLATFORM_MAP 声明顺序）。 */
const PLATFORM_ORDER = Object.keys(PLATFORM_MAP);

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // 预警（F1，后端并行开发中：失败仅降级为空态）
  const [alerts, setAlerts] = useState<AlertItemData[] | null>(null);
  const [alertsFailed, setAlertsFailed] = useState(false);
  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null);

  // 收录漏斗（F5，批 5 端点可能未就绪：404/空 → 空态）
  const [ledger, setLedger] = useState<LedgerSummary | null>(null);
  const [ledgerFailed, setLedgerFailed] = useState(false);

  // AI 可见性（迷你矩阵 + 均值指标卡）
  const [visibility, setVisibility] = useState<AiVisibility[] | null>(null);
  const [visibilityFailed, setVisibilityFailed] = useState(false);

  // 从全局 companyStore 读取级联选中态
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedBrandId = useCompanyStore((s) => s.selectedBrandId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // 传入 selectedCompanyId 按公司过滤统计数据
      const result = await getDashboard(selectedCompanyId || undefined);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取看板数据失败");
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId]);

  /** 预警列表 + 汇总：独立失败域，不阻断页面。 */
  const fetchAlerts = useCallback(async () => {
    setAlertsFailed(false);
    try {
      const [list, summary] = await Promise.all([
        getAlerts({
          brand_id: selectedBrandId || undefined,
          size: 10,
        }),
        getAlertSummary(selectedBrandId || undefined),
      ]);
      // 仅未处理（level!=resolved 语义即 status!=resolved），P0 优先 + 时间倒序
      const open = (list.items || [])
        .filter((a) => a.status !== "resolved")
        .sort((a, b) => {
          if (a.level !== b.level) return a.level === "P0" ? -1 : 1;
          return (b.last_triggered_at || "").localeCompare(
            a.last_triggered_at || "",
          );
        });
      setAlerts(open);
      setAlertSummary(summary);
    } catch {
      setAlerts(null);
      setAlertSummary(null);
      setAlertsFailed(true);
    }
  }, [selectedBrandId]);

  /** 收录漏斗：批 5 端点，404/异常 → 空态降级。 */
  const fetchLedger = useCallback(async () => {
    if (!selectedProductId) {
      setLedger(null);
      setLedgerFailed(true); // 无产品上下文时同样走空态
      return;
    }
    setLedgerFailed(false);
    try {
      const summary = await getIndexLedgerSummary(selectedProductId);
      setLedger(summary);
    } catch {
      setLedger(null);
      setLedgerFailed(true);
    }
  }, [selectedProductId]);

  /** AI 可见性：复用现有接口渲染迷你矩阵。 */
  const fetchVisibility = useCallback(async () => {
    setVisibilityFailed(false);
    try {
      const items = await getAiVisibility(selectedCompanyId || undefined);
      setVisibility(items);
    } catch {
      setVisibility(null);
      setVisibilityFailed(true);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);
  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);
  useEffect(() => {
    fetchVisibility();
  }, [fetchVisibility]);

  /** 标记处理成功：本地置 resolved + 汇总计数同步 -1。 */
  const handleAlertResolved = useCallback((id: string) => {
    setAlerts((prev) => {
      if (!prev) return prev;
      const target = prev.find((a) => a.id === id);
      if (target) {
        setAlertSummary((s) =>
          s
            ? {
                ...s,
                p0_count: target.level === "P0" ? Math.max(0, s.p0_count - 1) : s.p0_count,
                p1_count: target.level === "P1" ? Math.max(0, s.p1_count - 1) : s.p1_count,
              }
            : s,
        );
      }
      return prev.map((a) =>
        a.id === id ? { ...a, status: "resolved" as const } : a,
      );
    });
  }, []);

  /**
   * 迷你矩阵数据：品牌 × 平台，取每格最新一条记录。
   * 行按最差格升序（差距最大优先，对齐概念稿图例注），最多 6 行。
   */
  const matrix = useMemo(() => {
    if (!visibility || visibility.length === 0) return null;
    const latest = new Map<string, AiVisibility>();
    for (const item of visibility) {
      const brand = item.brand_name || item.brand_id || "未知品牌";
      const key = `${brand}@@${item.platform}`;
      const prev = latest.get(key);
      if (!prev || (item.recorded_at || "") > (prev.recorded_at || "")) {
        latest.set(key, item);
      }
    }
    const platforms = PLATFORM_ORDER.filter((p) =>
      visibility.some((i) => i.platform === p),
    );
    // 兜底：存在映射外平台时追加在末尾
    for (const i of visibility) {
      if (!platforms.includes(i.platform)) platforms.push(i.platform);
    }
    const rowMap = new Map<string, Record<string, number | null>>();
    for (const item of latest.values()) {
      const brand = item.brand_name || item.brand_id || "未知品牌";
      if (!rowMap.has(brand)) rowMap.set(brand, {});
      rowMap.get(brand)![item.platform] = item.mention_rate;
    }
    const rows = [...rowMap.entries()]
      .map(([name, cells]) => {
        const vals = Object.values(cells).filter(
          (v): v is number => v !== null && v !== undefined,
        );
        return { name, cells, min: vals.length ? Math.min(...vals) : 999 };
      })
      .sort((a, b) => a.min - b.min)
      .slice(0, 6);
    const updatedAt = visibility.reduce<string | null>(
      (acc, i) =>
        !acc || (i.recorded_at || "") > acc ? i.recorded_at || acc : acc,
      null,
    );
    return { platforms, rows, updatedAt };
  }, [visibility]);

  /** AI 可见性均值（最新格口径，与迷你矩阵一致）。 */
  const avgVisibility = useMemo(() => {
    if (!matrix) return null;
    const vals: number[] = [];
    for (const row of matrix.rows) {
      for (const v of Object.values(row.cells)) {
        if (v !== null && v !== undefined) vals.push(v);
      }
    }
    if (vals.length === 0) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [matrix]);

  if (loading) {
    return (
      <Box>
        <PageHeader title="情报总览" />
        <Loading fullPage text="正在加载情报数据..." />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <PageHeader title="情报总览" />
        <Card>
          <CardContent>
            <EmptyState
              text={error || "暂无数据"}
              subtitle="请稍后重试"
              action={{ label: "重新加载", onClick: fetchDashboard }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  const { stats, product_ranking, recent_activities, delivery_overview } = data;

  /** 最近动态的最新时间（更新胶囊用；无动态则回退当前时间）。 */
  const latestActivityTime = recent_activities.reduce<string | null>(
    (latest, a) => {
      if (!a.time) return latest;
      return !latest || a.time > latest ? a.time : latest;
    },
    null,
  );

  return (
    <Box>
      <PageHeader
        title="情报总览"
        subtitle="过去 7 天 AI 搜索生态情报 · 预警驱动运营动作"
        breadcrumbs={[{ label: "总览" }, { label: "情报总览" }]}
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UpdateCapsule
              updatedAt={latestActivityTime || new Date()}
              sampleN={recent_activities.length}
              sampleUnit="条动态"
            />
            {/* 导出汇报：占位按钮（汇报导出能力后续批次接入） */}
            <Button variant="outlined" size="small" disabled>
              导出汇报
            </Button>
          </Box>
        }
      />

      {/* ===== 指标卡 ×4 ===== */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <StatCard
            title="AI 可见性（平台均值）"
            value={avgVisibility === null ? "—" : avgVisibility.toFixed(1)}
            subtitle={avgVisibility === null ? "洞悉引擎未产出" : "品牌×平台提及率均值"}
            icon={<VisibilityOutlined />}
            iconBg="brand.50"
            iconColor="primary.main"
          />
        </Box>
        <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <StatCard
            title="本周新增预警"
            value={alertSummary ? alertSummary.week_new : "—"}
            subtitle={
              alertSummary
                ? `P0 ×${alertSummary.p0_count} · P1 ×${alertSummary.p1_count}`
                : "预警接口未接入"
            }
            icon={<NotificationsActiveOutlined />}
            iconBg="semanticBg.error"
            iconColor="error.main"
          />
        </Box>
        <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <StatCard
            title="投放内容收录率"
            value={ledger ? (ledger.index_rate * 100).toFixed(1) : "—"}
            suffix={ledger ? "%" : undefined}
            subtitle={
              ledger
                ? `${ledger.indexed}/${ledger.produced} 篇`
                : "收录管道未接入"
            }
            icon={<FactCheckOutlined />}
            iconBg="semanticBg.success"
            iconColor="success.main"
          />
        </Box>
        <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <StatCard
            title="本月投放费用"
            value={formatCurrency(stats.monthly_delivery_confirmed)}
            subtitle={`待确认 ${formatCurrency(stats.monthly_delivery_pending)}`}
            icon={<PaymentsOutlined />}
            iconBg="semanticBg.info"
            iconColor="info.main"
          />
        </Box>
      </Box>

      {/* ===== 预警流 + 漏斗（g83） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 预警流（左 2/3） */}
        <Grid item xs={12} md={8}>
          <Card id="alert-feed" sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">预警流</Typography>
                {alertSummary && (
                  <>
                    <Badge label={`P0 ×${alertSummary.p0_count}`} variant="error" />
                    <Badge label={`P1 ×${alertSummary.p1_count}`} variant="warning" />
                  </>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {/* 暂无全部预警页：锚到本卡 */}
                <Link
                  href="#alert-feed"
                  sx={{ fontSize: 12, color: "primary.main", textDecoration: "none" }}
                >
                  查看全部预警 ›
                </Link>
              </Box>
              {alerts === null && !alertsFailed ? (
                <Loading text="正在加载预警..." />
              ) : alertsFailed ? (
                <EmptyState
                  text="预警流暂未接入"
                  source="由预警引擎按规则周期巡检产出，联调完成后自动展示"
                />
              ) : alerts && alerts.length === 0 ? (
                <EmptyState
                  text="暂无未处理预警"
                  source="由预警引擎按规则周期巡检产出"
                  hint="P0/P1 规则触发后自动出现在这里"
                />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {(alerts || []).slice(0, 5).map((a) => (
                    <AlertItem key={a.id} item={a} onResolved={handleAlertResolved} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 三层漏斗（右 1/3） */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                三层漏斗 · 本月
              </Typography>
              {ledger === null && !ledgerFailed ? (
                <Loading text="正在加载漏斗..." />
              ) : ledgerFailed || !ledger ? (
                /* 批 5 端点未就绪/无产品上下文：优雅降级空态，不报错 */
                <EmptyState
                  text="收录漏斗暂未接入"
                  source="由洞悉引擎 v1.5 收录管道产出，未接入时显示空态"
                  hint={
                    selectedProductId
                      ? undefined
                      : "请先在顶栏选择产品"
                  }
                />
              ) : (
                <>
                  <FunnelView summary={ledger} drilldownLink="/index-ledger" />
                  {ledger.index_rate < ledger.baseline_index_rate && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        lineHeight: 1.8,
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: "1px solid",
                        borderColor: "lineSoft",
                      }}
                    >
                      收录率低于基线{" "}
                      {(ledger.baseline_index_rate * 100).toFixed(0)}
                      %，断点集中在搜索引擎收录层，修复后可拉动 AI 可检索层。
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== 迷你矩阵 + 最近动态（g83） ===== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 迷你矩阵（左 2/3）：品牌 × 平台热力速览 */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">AI 可见性 · 品牌 × 平台速览</Typography>
                {matrix?.updatedAt && (
                  <UpdateCapsule updatedAt={matrix.updatedAt} status="info" />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Link
                  component="a"
                  onClick={() => navigate("/ai-visibility")}
                  sx={{ fontSize: 12, color: "primary.main", textDecoration: "none", cursor: "pointer" }}
                >
                  进全景矩阵 ›
                </Link>
              </Box>
              {visibility === null && !visibilityFailed ? (
                <Loading text="正在加载可见性数据..." />
              ) : visibilityFailed || !matrix || matrix.rows.length === 0 ? (
                <EmptyState
                  text="暂无可见性数据"
                  source="由洞悉引擎周期探测产出"
                  hint="话题 × 平台全景矩阵随批 4 矩阵端点接入"
                />
              ) : (
                <>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 3,
                      overflowX: "auto",
                    }}
                  >
                    <Box
                      component="table"
                      sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
                    >
                      <Box component="thead">
                        <Box component="tr">
                          <Box
                            component="th"
                            sx={{
                              textAlign: "left",
                              px: 2,
                              height: 36,
                              bgcolor: "grey.50",
                              fontSize: 12,
                              color: "text.secondary",
                              fontWeight: 600,
                            }}
                          >
                            品牌
                          </Box>
                          {matrix.platforms.map((p) => (
                            <Box
                              component="th"
                              key={p}
                              sx={{
                                px: 1,
                                height: 36,
                                bgcolor: "grey.50",
                                fontSize: 12,
                                color: "text.secondary",
                                fontWeight: 600,
                                textAlign: "center",
                              }}
                            >
                              {platformLabel(p)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {matrix.rows.map((row) => (
                          <Box component="tr" key={row.name}>
                            <Box
                              component="th"
                              sx={{
                                textAlign: "left",
                                px: 2,
                                py: 0.5,
                                fontWeight: 600,
                                fontSize: 13,
                                color: "text.primary",
                                borderTop: "1px solid",
                                borderColor: "lineSoft",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.name}
                            </Box>
                            {matrix.platforms.map((p) => {
                              const v = row.cells[p];
                              const lv = HEAT_LEVELS[heatLevel(v)];
                              return (
                                <Box
                                  component="td"
                                  key={p}
                                  sx={{
                                    p: 0.5,
                                    borderTop: "1px solid",
                                    borderColor: "lineSoft",
                                    textAlign: "center",
                                  }}
                                >
                                  <Box
                                    title={
                                      v === null || v === undefined
                                        ? "无数据"
                                        : `提及率 ${v.toFixed(1)}%`
                                    }
                                    sx={{
                                      height: 36,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: 1,
                                      fontWeight: 600,
                                      fontSize: 13,
                                      // 色阶集中定义于 constants.ts HEAT_LEVELS（对齐 theme.css §1.3）
                                      bgcolor: lv.bg,
                                      color: lv.text,
                                    }}
                                  >
                                    {v === null || v === undefined ? "—" : Math.round(v)}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  {/* 图例（对齐概念稿 matrix-legend；色阶来自 HEAT_LEVELS） */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      fontSize: 12,
                      color: "text.secondary",
                      mt: 1.5,
                    }}
                  >
                    {[
                      { lv: 1, label: "1–20 差" },
                      { lv: 2, label: "21–50 中" },
                      { lv: 3, label: "51–80 良" },
                      { lv: 4, label: "81–100 优" },
                    ].map(({ lv, label }) => (
                      <Box key={lv} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: 0.5,
                            bgcolor: HEAT_LEVELS[lv as 1 | 2 | 3 | 4].bg,
                          }}
                        />
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                    <Typography
                      sx={{ fontSize: 12, color: "text.disabled", ml: "auto" }}
                    >
                      排序：差距最大优先
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 最近动态（右 1/3，保留现有） */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>最近动态</Typography>
              {recent_activities.length === 0 ? (
                <EmptyState
                  text="暂无动态"
                  source="动态来自诊断与投放事件 · 产生后自动展示"
                />
              ) : (
                <List dense>
                  {recent_activities.map((activity, index) => (
                    <ListItem
                      key={index}
                      sx={{ px: 0, py: 1, alignItems: "flex-start" }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Badge
                            label={activity.type}
                            variant={
                              activity.type.includes("诊断") ? "info" :
                              activity.type.includes("确认") ? "success" :
                              activity.type.includes("待确认") ? "warning" : "default"
                            }
                          />
                          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                            {formatRelativeTime(activity.time)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                          {activity.title}
                        </Typography>
                        {activity.description && (
                          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                            {activity.description}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== 产品 GHS 排行（保留，下移） ===== */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h4">产品 GHS 排行</Typography>
            <TrendingUpOutlined sx={{ color: "primary.main", fontSize: 18 }} />
          </Box>
          {product_ranking.length === 0 ? (
            <EmptyState
              text="暂无产品数据"
              source="产品资产来自资产录入"
              action={{ label: "去录入产品", onClick: () => navigate("/products") }}
            />
          ) : (
            <List dense>
              {product_ranking.slice(0, 10).map((item, index) => (
                <ListItem
                  key={item.product_id}
                  sx={{
                    px: 0,
                    py: 1,
                    borderBottom: index < product_ranking.length - 1 ? "1px solid" : "none",
                    borderColor: "lineSoft",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                    <Typography
                      sx={{
                        width: 24,
                        fontSize: 14,
                        fontWeight: 700,
                        color: index < 3 ? "primary.main" : "text.secondary",
                        textAlign: "center",
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                        {item.product_name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                        {item.brand_name}
                        {item.category ? ` · ${item.category}` : ""}
                      </Typography>
                    </Box>
                    {item.ghs_change !== null && item.ghs_change !== undefined && (
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: item.ghs_change >= 0 ? "success.main" : "error.main",
                        }}
                      >
                        {item.ghs_change >= 0 ? "↑" : "↓"}
                        {Math.abs(item.ghs_change)}
                      </Typography>
                    )}
                    <GhsRing score={item.ghs_score} size={44} thickness={4} />
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* ===== 投放费用概览（保留） ===== */}
      <Card>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 2 }}>投放费用概览</Typography>
          {delivery_overview.length === 0 ? (
            <EmptyState
              text="暂无投放数据"
              source="由投放计划确认后产出"
              action={{ label: "查看投放计划", onClick: () => navigate("/delivery-plans") }}
            />
          ) : (
            <List dense>
              {delivery_overview.map((item, index) => {
                const statusInfo = DELIVERY_STATUS_MAP[item.status] || {
                  label: item.status,
                  color: "text.secondary",
                  bg: "background.default",
                };
                return (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0,
                      py: 1,
                      borderBottom: index < delivery_overview.length - 1 ? "1px solid" : "none",
                      borderColor: "lineSoft",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {(item.product_name_snapshot ||
                            (item.product_name && item.product_name !== "未知产品"
                              ? item.product_name
                              : "（已删除产品）"))}{" "}
                          · {item.media_name}
                        </Typography>
                        {item.ghs_change !== null && item.ghs_change !== undefined && (
                          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                            GHS 变化：{item.ghs_change >= 0 ? "+" : ""}{item.ghs_change}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                        {formatCurrency(item.amount)}
                      </Typography>
                      <Badge
                        label={statusInfo.label}
                        color={statusInfo.color}
                        bgColor={statusInfo.bg}
                      />
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
